/**
 * Accessible hero slideshow behavior.
 *
 * @param {Document} documentObject Document implementation.
 * @param {Window} windowObject Window implementation.
 * @return {object|null} Slideshow controls for testing or null when unavailable.
 */
export function initHeroSlideshow(documentObject = document, windowObject = window) {
	const SLIDE_INTERVAL = 8000;
	const SWIPE_THRESHOLD = 50;
	const heroSection = documentObject.querySelector('.simpatico-hero--slideshow');

	if (!heroSection) return null;

	const slides = Array.from(heroSection.querySelectorAll('.simpatico-hero__slide'));
	const indicators = Array.from(heroSection.querySelectorAll('.simpatico-hero__indicator'));

	if (slides.length <= 1 || indicators.length !== slides.length) return null;

	let currentSlide = Math.max(0, slides.findIndex(slide => slide.classList.contains('simpatico-hero__slide--active')));
	let slideTimer = null;
	let touchStartX = 0;
	let touchStartY = 0;
	let isSwiping = false;
	let isPointerInside = false;
	let isFocusInside = false;
	const autoplayEnabled = !windowObject.matchMedia('(prefers-reduced-motion: reduce)').matches;

	function hydrateSlide(slide) {
		slide.querySelectorAll('[data-srcset], [data-src]').forEach((element) => {
			if (element.dataset.srcset) {
				element.setAttribute('srcset', element.dataset.srcset);
				element.removeAttribute('data-srcset');
			}
			if (element.dataset.sizes) {
				element.setAttribute('sizes', element.dataset.sizes);
				element.removeAttribute('data-sizes');
			}
			if (element.dataset.src) {
				element.setAttribute('loading', 'eager');
				element.setAttribute('src', element.dataset.src);
				element.removeAttribute('data-src');
			}
		});
	}

	function syncAccessibleState() {
		slides.forEach((slide, index) => {
			const isActive = index === currentSlide;
			slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
			slide.toggleAttribute('inert', !isActive);
		});

		indicators.forEach((indicator, index) => {
			if (index === currentSlide) {
				indicator.setAttribute('aria-current', 'true');
			} else {
				indicator.removeAttribute('aria-current');
			}
		});
	}

	function pauseTimer() {
		if (!slideTimer) return;

		windowObject.clearInterval(slideTimer);
		slideTimer = null;
	}

	function resetTimer() {
		pauseTimer();
		if (!autoplayEnabled || documentObject.hidden || isPointerInside || isFocusInside) {
			return;
		}

		slideTimer = windowObject.setInterval(nextSlide, SLIDE_INTERVAL);
	}

	function normalizeIndex(index) {
		return ((index % slides.length) + slides.length) % slides.length;
	}

	function goToSlide(index) {
		const nextIndex = normalizeIndex(index);
		if (nextIndex === currentSlide) {
			resetTimer();
			return;
		}

		hydrateSlide(slides[nextIndex]);
		slides[currentSlide].classList.remove('simpatico-hero__slide--active');
		indicators[currentSlide].classList.remove('simpatico-hero__indicator--active');
		currentSlide = nextIndex;
		slides[currentSlide].classList.add('simpatico-hero__slide--active');
		indicators[currentSlide].classList.add('simpatico-hero__indicator--active');
		syncAccessibleState();

		windowObject.requestAnimationFrame(() => {
			windowObject.dispatchEvent(new windowObject.Event('simpatico:hero:update-metrics'));
		});

		resetTimer();
	}

	function nextSlide() {
		goToSlide(currentSlide + 1);
	}

	function previousSlide() {
		goToSlide(currentSlide - 1);
	}

	function handleTouchStart(event) {
		touchStartX = event.changedTouches[0].screenX;
		touchStartY = event.changedTouches[0].screenY;
		isSwiping = false;
	}

	function handleTouchMove(event) {
		const diffX = Math.abs(event.changedTouches[0].screenX - touchStartX);
		const diffY = Math.abs(event.changedTouches[0].screenY - touchStartY);

		if (diffX > diffY && diffX > 10) {
			isSwiping = true;
			event.preventDefault();
		}
	}

	function handleTouchEnd(event) {
		if (!isSwiping) return;

		const diffX = touchStartX - event.changedTouches[0].screenX;
		if (Math.abs(diffX) <= SWIPE_THRESHOLD) return;

		if (diffX > 0) {
			nextSlide();
		} else {
			previousSlide();
		}
	}

	heroSection.addEventListener('touchstart', handleTouchStart, { passive: true });
	heroSection.addEventListener('touchmove', handleTouchMove, { passive: false });
	heroSection.addEventListener('touchend', handleTouchEnd, { passive: true });
	heroSection.addEventListener('mouseenter', () => {
		isPointerInside = true;
		pauseTimer();
	});
	heroSection.addEventListener('mouseleave', () => {
		isPointerInside = false;
		resetTimer();
	});
	heroSection.addEventListener('focusin', () => {
		isFocusInside = true;
		pauseTimer();
	});
	heroSection.addEventListener('focusout', event => {
		if (!heroSection.contains(event.relatedTarget)) {
			isFocusInside = false;
			resetTimer();
		}
	});

	indicators.forEach((indicator, index) => {
		indicator.addEventListener('click', () => goToSlide(index));
	});

	documentObject.addEventListener('visibilitychange', () => {
		if (documentObject.hidden) {
			pauseTimer();
		} else {
			resetTimer();
		}
	});

	syncAccessibleState();
	resetTimer();

	return {
		getCurrentSlide: () => currentSlide,
		goToSlide,
		isAutoplayEnabled: () => autoplayEnabled,
		pauseTimer,
	};
}
